// Phase-A R5 — Brand panel for the redesigned login (lg+ only).
// Knowledge-graph backdrop + auroras + testimonial + editorial headline + stats.
// Visible only at >720px; hidden entirely on phone via the .r5-login-shell
// responsive CSS. Internally dark-themed so the white-on-navy palette holds
// regardless of the user's app-wide theme.
//
// S-I18N-W4C — کپیِ این پنل تا امروز هاردکد بود، پس صفحهٔ ورودِ عربی نیمی
// فارسی می‌ماند (شاهد: اسکرین‌شاتِ مالک از /login روی ar). همه‌چیز به
// namespaceِ `identity` منتقل شد؛ ارقامِ آمار هم دیگر «۱۲۴+» و «۹۹٫۹٪»ِ
// دستی نیستند بلکه آرگومانِ عددیِ ICU‌اند تا CLDR خودش ۰-۹ (fa) یا ٠-٩ (ar)
// بدهد. تنها متنی که ترجمه نمی‌شود `BRAND.developer.legalFa` در فوترِ
// کنارِ همین پنل است — متنِ مصوبِ حقوقی، تصمیمِ مالک نه تصمیمِ مهندسی.
import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { KnowledgeGraph } from "../../components/KnowledgeGraph";
import { Eyebrow } from "./login-atoms";
import { BRAND } from "../../brand/brand.config";

// three.js is LAZY: this React.lazy import makes Vite emit BrandMark3D + three
// as a SEPARATE chunk that downloads ONLY when the brand panel mounts (the
// auth/login route). The main app bundle is unchanged. The Suspense fallback
// below shows a crisp static leaf, so the slot is never blank while it loads.
const BrandMark3D = React.lazy(() => import("./BrandMark3D"));

/** Crisp static mark — the Suspense fallback (and what shows pre-hydration). */
const MarkStaticFallback: React.FC<{ size: number }> = ({ size }) => (
  <img
    src={BRAND.assets.markLogin192}
    alt=""
    aria-hidden="true"
    width={size}
    height={size}
    style={{
      width: size,
      height: "auto",
      filter:
        "drop-shadow(0 0 26px rgba(77,157,224,0.45)) drop-shadow(0 0 60px rgba(236,219,0,0.30))",
    }}
  />
);

const TestimonialCard: React.FC = () => {
  const { t } = useTranslation("identity");
  return (
    <div
    style={{
      background: "color-mix(in oklch, var(--r5-paper) 92%, transparent)",
      backdropFilter: "blur(8px)",
      border: "1px solid var(--r5-line)",
      borderRadius: 16,
      padding: "16px 18px",
      maxWidth: 360,
      boxShadow: "0 24px 40px -24px rgba(0,0,0,.25)",
      animation: "r5-card-in 800ms cubic-bezier(.22,1,.36,1) 200ms both",
    }}
  >
    <div style={{ display: "flex", gap: 4, marginBottom: 10 }} aria-label={t("login.brandPanel.testimonial.starsAria")}>
      {[0, 1, 2, 3, 4].map((i) => (
        <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="var(--r5-gold)" aria-hidden="true">
          <path d="m12 2 3.1 6.3 6.9 1L17 14.1l1.2 7L12 17.8 5.8 21l1.2-6.9L2 9.3l7-1L12 2Z" />
        </svg>
      ))}
    </div>
    <p style={{ margin: 0, fontSize: 14, lineHeight: 1.75, color: "var(--r5-ink-2)" }}>
      {t("login.brandPanel.testimonial.quote")}
    </p>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: "50%",
          background: "linear-gradient(135deg, var(--r5-sage), var(--r5-sage-deep))",
          color: "var(--r5-paper)",
          display: "grid",
          placeItems: "center",
          fontWeight: 700,
          fontSize: 13,
        }}
        aria-hidden
      >
        {t("login.brandPanel.testimonial.authorInitials")}
      </div>
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{t("login.brandPanel.testimonial.authorName")}</span>
        <span style={{ fontSize: 11, color: "var(--r5-muted)" }}>{t("login.brandPanel.testimonial.authorRole")}</span>
      </div>
    </div>
    </div>
  );
};

const StatRow: React.FC = () => {
  const { t } = useTranslation("identity");
  // عددها آرگومانِ ICU‌اند، نه رشتهٔ آماده: fa رقمِ ۰-۹ و جداکنندهٔ ٫ می‌گیرد و
  // ar رقمِ ٠-٩ — همان چیزی که پیش‌تر با نوشتنِ دستیِ «۹۹٫۹٪» ممکن نبود.
  //
  // نامِ آرگومان عمداً `value` است نه `count`: i18next خودش `count` را برایِ
  // جمع‌بندی می‌قاپد و با درون‌ریزیِ خودش جایگزین می‌کند، پس عدد هرگز به
  // فرمت‌کنندهٔ ICU نمی‌رسد و «210K» لاتین بیرون می‌آید.
  const stats: Array<{ k: string; v: string; l: string }> = [
    { k: "tenants", v: t("login.brandPanel.stats.tenantsValue", { value: 124 }), l: t("login.brandPanel.stats.tenantsLabel") },
    { k: "learners", v: t("login.brandPanel.stats.learnersValue", { value: 210 }), l: t("login.brandPanel.stats.learnersLabel") },
    { k: "uptime", v: t("login.brandPanel.stats.uptimeValue", { value: 99.9 }), l: t("login.brandPanel.stats.uptimeLabel") },
  ];
  return (
    <div
      style={{
        marginTop: 28,
        display: "flex",
        gap: 24,
        flexWrap: "wrap",
        paddingTop: 18,
        borderTop: "1px solid var(--r5-line)",
      }}
    >
      {stats.map((s) => (
        <div key={s.k} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: "var(--r5-ink)" }}>{s.v}</span>
          <span style={{ fontSize: 11, color: "var(--r5-muted)", fontFamily: "var(--r5-mono)", letterSpacing: "0.08em" }}>
            {s.l}
          </span>
        </div>
      ))}
    </div>
  );
};

// If the lazy three scene throws at render time (or its chunk fails to load),
// fall back to the crisp static leaf instead of crashing the login page.
class LeafBoundary extends React.Component<
  { size: number; children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (this.state.failed) return <MarkStaticFallback size={this.props.size} />;
    return this.props.children;
  }
}

/** پهنای ثابتِ صحنهٔ three.js. هرگز عوض نمی‌شود تا صحنه دوباره ساخته نشود. */
const MARK_SCENE = 460;
/** سهمِ خودِ نشان از بومِ صحنه (بقیه هالهٔ شفاف است) — از هندسهٔ دوربین درمی‌آید. */
const MARK_FILL = 0.71;
/** پهنای امضا نسبت به بومِ صحنه و ابعاد واقعی PNG (IHDR: ۷۲۰×۲۸۵). */
const SIG_FILL = 0.66;
const SIG_WIDTH = 720;
const SIG_HEIGHT = 285;
const SIG_RATIO = SIG_HEIGHT / SIG_WIDTH;
const LOCKUP_GAP = 16;
const MIN_LOCKUP_SCALE = 0.45;

/**
 * Fit the decorative lockup between the actual translated text blocks.
 * Fixed source-language heights overlap longer translated headlines. Reserve
 * the existing minimum mark size by growing the panel in normal page flow,
 * never by clipping copy or changing the fixed-size three.js scene.
 */
const useLockupLayout = (
  panelRef: React.RefObject<HTMLElement>,
  topRef: React.RefObject<HTMLDivElement>,
  copyRef: React.RefObject<HTMLDivElement>,
  layoutKey: string,
): { scale: number; top: number; minHeight: number; visible: boolean } => {
  const [layout, setLayout] = React.useState({ scale: 0, top: 0, minHeight: 0, visible: false });
  React.useLayoutEffect(() => {
    const panel = panelRef.current;
    const topBlock = topRef.current;
    const copyBlock = copyRef.current;
    if (!panel || !topBlock || !copyBlock) return;
    const calc = (): void => {
      const panelBox = panel.getBoundingClientRect();
      if (panelBox.width <= 0 || panelBox.height <= 0) {
        // CSS-hidden panels must not retain decorative WebGL or SVG RAF loops.
        // Reuse the measured layout observer, not a duplicate breakpoint rule.
        setLayout((previous) => previous.visible ? { ...previous, visible: false } : previous);
        return;
      }
      const topBox = topBlock.getBoundingClientRect();
      const copyBox = copyBlock.getBoundingClientRect();
      const style = getComputedStyle(panel);
      const padding = (property: string): number => Number.parseFloat(style.getPropertyValue(property)) || 0;
      const blockPerScale = MARK_SCENE * (MARK_FILL + SIG_FILL * SIG_RATIO);
      const minHeight = Math.ceil(topBox.height + copyBox.height + padding("padding-top") + padding("padding-bottom")
        + blockPerScale * MIN_LOCKUP_SCALE + 6 + LOCKUP_GAP * 2);
      const start = topBox.bottom - panelBox.top + LOCKUP_GAP;
      const end = copyBox.top - panelBox.top - LOCKUP_GAP;
      const byHeight = Math.max(0, end - start - 6) / blockPerScale;
      const byWidth = Math.max(0, panelBox.width - padding("padding-left") - padding("padding-right")) / (MARK_SCENE * MARK_FILL);
      // Round down so the measured gap is never exceeded. ResizeObserver also
      // responds to catalogue/font wrapping without remounting the 3D scene.
      const scale = Math.floor(Math.min(1, byHeight, byWidth) * 20) / 20;
      const top = Math.round((start + end) * 50) / 100;
      setLayout((previous) => previous.visible && previous.scale === scale && previous.top === top && previous.minHeight === minHeight
        ? previous : { scale, top, minHeight, visible: true });
    };
    calc();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(calc);
    for (const element of [panel, topBlock, copyBlock]) observer?.observe(element);
    window.addEventListener("resize", calc);
    return () => { observer?.disconnect(); window.removeEventListener("resize", calc); };
  }, [panelRef, topRef, copyRef, layoutKey, layout.minHeight]);
  return layout;
};

export interface BrandPanelProps {
  showTestimonial?: boolean;
}

export const BrandPanel: React.FC<BrandPanelProps> = ({ showTestimonial = true }) => {
  const { t, i18n } = useTranslation("identity");
  const panelRef = React.useRef<HTMLElement>(null);
  const topRef = React.useRef<HTMLDivElement>(null);
  const copyRef = React.useRef<HTMLDivElement>(null);
  const { scale, top, minHeight, visible } = useLockupLayout(panelRef, topRef, copyRef, `${i18n.resolvedLanguage}:${showTestimonial}`);
  const markFoot = Math.round(MARK_SCENE * MARK_FILL * scale);
  const sigWidth = Math.round(MARK_SCENE * SIG_FILL * scale);

  // Pin the brand panel to its own internal dark palette by overriding the
  // R5-scoped vars on this aside only. The form panel's theme stays whatever
  // the user picked; the brand panel is always the marketing-dark aesthetic.
  const brandVars: React.CSSProperties = {
    ["--r5-bg-2" as unknown as keyof React.CSSProperties]: "#0a1730",
    ["--r5-paper" as unknown as keyof React.CSSProperties]: "#0f1f3d",
    ["--r5-ink" as unknown as keyof React.CSSProperties]: "#eef3ff",
    ["--r5-ink-2" as unknown as keyof React.CSSProperties]: "#c8d3eb",
    ["--r5-muted" as unknown as keyof React.CSSProperties]: "#8092b3",
    ["--r5-line" as unknown as keyof React.CSSProperties]: "rgba(238,243,255,0.10)",
    ["--r5-line-strong" as unknown as keyof React.CSSProperties]: "rgba(238,243,255,0.22)",
    ["--r5-sage" as unknown as keyof React.CSSProperties]: "#7da5ff",
    ["--r5-sage-deep" as unknown as keyof React.CSSProperties]: "#a8c2ff",
  } as React.CSSProperties;

  return (
    <aside
      ref={panelRef}
      className="r5-brand-panel"
      style={{
        ...brandVars,
        position: "sticky",
        top: 0,
        alignSelf: "start",
        height: "100dvh",
        minHeight: minHeight || undefined,
        boxSizing: "border-box",
        color: "#eef3ff",
        background:
          "radial-gradient(120% 90% at 100% 0%, #1a3a7a 0%, #0d1f44 40%, #060e22 100%)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        padding: "clamp(20px, 4vw, 48px)",
        isolation: "isolate",
      }}
    >
      {visible && <KnowledgeGraph accent="#7da5ff" />}

      {/* Aurora glow A */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          insetInlineStart: "-15%",
          top: "-20%",
          width: "70%",
          aspectRatio: "1",
          background: "radial-gradient(closest-side, rgba(125,165,255,0.32), transparent 70%)",
          filter: "blur(40px)",
          pointerEvents: "none",
          animation: "r5-aurora-1 24s ease-in-out infinite alternate",
          zIndex: 0,
        }}
      />
      {/* Aurora glow B */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          insetInlineEnd: "-10%",
          bottom: "-15%",
          width: "60%",
          aspectRatio: "1",
          background: "radial-gradient(closest-side, rgba(64,108,200,0.30), transparent 70%)",
          filter: "blur(50px)",
          pointerEvents: "none",
          animation: "r5-aurora-2 30s ease-in-out infinite alternate",
          zIndex: 0,
        }}
      />

      {/* Vignette + grid */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.25) 100%)," +
            "repeating-linear-gradient(0deg, transparent 0 38px, rgba(255,255,255,0.025) 38px 39px)," +
            "repeating-linear-gradient(90deg, transparent 0 38px, rgba(255,255,255,0.025) 38px 39px)",
          pointerEvents: "none",
          maskImage: "radial-gradient(120% 80% at 60% 40%, black 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(120% 80% at 60% 40%, black 30%, transparent 75%)",
          zIndex: 0,
        }}
      />

      {/* Top: testimonial */}
      <div ref={topRef} className="r5-brand-top" style={{ position: "relative", zIndex: 2 }}>
        {showTestimonial && <TestimonialCard />}
      </div>

      {/* Brand hero — prominent ANIMATED Jahad leaf (three.js, lazy chunk).
          Replaces the old 0.09-opacity JDO watermark. Decorative (aria-hidden),
          pointer-events:none so it never intercepts clicks on the form side.
          The whole thing degrades to a crisp static leaf when reduced-motion /
          WebGL is off / the three chunk fails (Suspense + LeafBoundary). */}
      <div
        className="r5-brand-leaf"
        aria-hidden="true"
        style={{
          position: "absolute",
          // Centered hero (was tucked on the right edge, half-behind the text + only 320px →
          // the animation barely showed). Now it's the brand panel's clear visual anchor in the
          // empty middle band between the top testimonial and the bottom headline.
          // PHYSICAL `left` (NOT insetInlineStart): the panel is RTL, where inset-inline-start
          // maps to `right` — combined with the physical `translate(-50%)` that pushed the leaf
          // off the left edge (clipped by the panel's overflow:hidden → "can't see the logo").
          // `left: 50%` + translateX(-50%) centers correctly in both directions.
          left: "50%",
          top,
          visibility: scale > 0 ? "visible" : "hidden",
          transform: "translate(-50%, -50%)",
          // ستونی شد تا امضای نستعلیق زیرِ نشان بنشیند و مجموع، همان لاک‌آپِ
          // رسمی خوانده شود.
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          opacity: 1,
          pointerEvents: "none",
          zIndex: 1,
        }}
      >
        {/* جعبه فقط به اندازهٔ ردِ پای دیدارِی نشان است، نه کلِ بومِ صحنه:
            ~۲۹٪ بومِ ۴۶۰px هالهٔ شفاف است و اگر در چیدمان حساب شود، امضا را
            بی‌دلیل ۱۳۰px پایین‌تر می‌بَرد. بوم با position:absolute بیرون از
            جریانِ چیدمان و وسط‌چین می‌ماند. */}
        <div
          style={{
            position: "relative",
            width: markFoot,
            height: markFoot,
            display: "grid",
            placeItems: "center",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: `translate(-50%, -50%) scale(${scale})`,
              width: MARK_SCENE,
              height: MARK_SCENE,
              display: "grid",
              placeItems: "center",
            }}
          >
            {visible ? <LeafBoundary size={MARK_SCENE}>
              <React.Suspense fallback={<MarkStaticFallback size={MARK_SCENE} />}>
                <BrandMark3D size={MARK_SCENE} />
              </React.Suspense>
            </LeafBoundary> : <MarkStaticFallback size={MARK_SCENE} />}
          </div>
        </div>

        {/* امضای نستعلیقِ رسمیِ سازمان. تصویر است نه متن، چون این خط یک امضای
            دست‌نویسِ برند است و هیچ فونتِ نستعلیقی آن را بازتولید نمی‌کند؛
            نسخهٔ سفید (on-dark) چون کلِ امضا در اصل سرمه‌ای است و روی پنلِ
            #0d1f44 ناپدید می‌شود. کلِ بلوک aria-hidden است، پس alt خالی. */}
        <img
          src={BRAND.assets.wordmarkOnDark720}
          alt=""
          width={SIG_WIDTH}
          height={SIG_HEIGHT}
          style={{
            width: sigWidth,
            height: "auto",
            marginTop: 6,
            opacity: 0.94,
            filter: "drop-shadow(0 2px 10px rgba(6,14,34,0.55))",
          }}
        />
      </div>

      {/* Bottom: editorial headline + stats */}
      <div ref={copyRef} className="r5-brand-copy" style={{ position: "relative", zIndex: 2 }}>
        <Eyebrow
          text={t("login.brandPanel.eyebrowText")}
          subtext={t("login.brandPanel.eyebrowSubtext")}
        />
        <h2
          style={{
            fontSize: "clamp(34px, 4.5vw, 56px)",
            lineHeight: 1.05,
            margin: "10px 0 4px",
            fontWeight: 800,
            letterSpacing: "-0.015em",
            maxWidth: "16ch",
            color: "#eef3ff",
          }}
        >
          {t("login.brandPanel.headlineLine1")}
          <br />
          {/* واژهٔ گرادیانی جزوِ همان جمله است، پس با <Trans/> می‌آید تا مترجم
              بتواند جایش را در جمله جابه‌جا کند؛ سه کلیدِ جدا این آزادی را
              می‌گرفت و در عربی ترتیبِ «على نطاق الجامعة» را می‌شکست. */}
          <Trans
            t={t}
            i18nKey="login.brandPanel.headlineLine2"
            components={{
              hl: (
                <span
                  style={{
                    background: "linear-gradient(120deg, #a8c2ff, #7da5ff 40%, #ffffff)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                />
              ),
            }}
          />
        </h2>
        <p
          style={{
            color: "rgba(238,243,255,0.72)",
            maxWidth: "44ch",
            fontSize: 14.5,
            lineHeight: 1.7,
            margin: "10px 0 0",
          }}
        >
          {/* نامِ سازمان و شعارش پارامترند نه بخشی از رشته: نامِ ثبتیِ شرکت
              است و در هیچ زبانی ترجمه نمی‌شود. */}
          {t("login.brandPanel.lede", { org: BRAND.org.nameFa, slogan: BRAND.org.sloganFa })}
        </p>
        <StatRow />
      </div>
    </aside>
  );
};

export default BrandPanel;
